import * as RadioGroup from '@radix-ui/react-radio-group';
import { head, isEmpty, map, take } from 'lodash';
import PropTypes from 'prop-types';
import { OptionPropType, SelectedOptionsPropType } from '@components/basting-selector/prop-types';
import { forwardRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Flex } from '@components/grid';
import { CANONICAL_CHOICE_MAPPING, CANONICAL_CHOICE_NAMES, colors } from '@nandosaus/constants';
import { Icon } from '@components/icons';
import { A } from '../../../typography/new';
import { ByoOptionCard } from '../byo-option-card';
import { ChoicePropType } from '../prop-types';
import { OptionCard, OptionLabel } from '../option-card';
import { OptionGroup } from '../option-group';

const proteinRegex = /protein/g;

const { DRINK } = CANONICAL_CHOICE_NAMES;

// Local styled component for black link text
const BlackA = styled(A)`
  color: ${colors.black};
`;

// Styled RadioGroup Item; borders applied for image-based lists (drinks) and size lists
const StyledRadioItem = styled(RadioGroup.Item)`
  display: flex;
  align-items: center;
  border-radius: 0;

  ${({ $withImage, $withSize, $isFirst }) =>
    ($withImage || $withSize) &&
    css`
      border-bottom: 1px solid ${colors.warmWhite3};
      padding: 10px 0;
      ${$isFirst &&
        css`
          border-top: 1px solid ${colors.warmWhite3};
        `}
    `};
`;

export const RadioChoiceType = forwardRef(
  ({ choice, selectedOptions, onChange, index, options, getItemValue, getItemLabel, isByo }, ref) => {
    const radioItems = options || choice.optionsForPicker;

    const isLabelChoice = !isEmpty(choice.id.match(proteinRegex));
    const isImageLabelChoice = CANONICAL_CHOICE_MAPPING[choice.name] === DRINK;

    const [displayExpanded, setDisplayExpanded] = useState(false);

    const isSizeChoice = /select size/i.test(choice.name);
    const isCollapsable = isImageLabelChoice;
    const items = isCollapsable && !displayExpanded ? take(radioItems, 5) : radioItems;

    if ((isLabelChoice || isImageLabelChoice || isSizeChoice) && !isByo) {
      return (
        <RadioGroup.Root value={head(selectedOptions) || null} orientation="vertical">
          <OptionGroup
            ref={ref}
            gap={isImageLabelChoice ? '0' : '16px'}
            index={index}
            isByo={isByo}
            verticalDisplay={isLabelChoice || isImageLabelChoice || isSizeChoice}
          >
            {map(items, (item, itemIndex) => {
              const value = getItemValue ? getItemValue(item) : item.value;
              const label = getItemLabel ? getItemLabel(item) : item.label;
              const isFirst = itemIndex === 0;

              return (
                <StyledRadioItem
                  value={value}
                  disabled={item.disabled}
                  asChild
                  onClick={() => onChange(value)}
                  key={value}
                  $withImage={isImageLabelChoice}
                  $withSize={isSizeChoice}
                  $isFirst={isFirst}
                >
                  <OptionLabel
                    option={item}
                    label={label}
                    showOptionImage={isImageLabelChoice}
                    isDrink={isImageLabelChoice}
                  />
                </StyledRadioItem>
              );
            })}
            {isCollapsable && (
              <Flex justifyContent="center" paddingTop="20px">
                <BlackA
                  textAlign="center"
                  onClick={() => setDisplayExpanded(!displayExpanded)}
                  display="flex"
                  alignItems="center"
                >
                  {displayExpanded ? 'View less' : 'View all drinks'}
                  <Icon
                    name="chevron"
                    width="30px"
                    height="30px"
                    color={colors.black}
                    transform={displayExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
                  />
                </BlackA>
              </Flex>
            )}
          </OptionGroup>
        </RadioGroup.Root>
      );
    }

    return (
      <RadioGroup.Root value={head(selectedOptions) || null} orientation="horizontal">
        <OptionGroup ref={ref} index={index} isByo={isByo}>
          {radioItems.map(item => {
            const value = getItemValue ? getItemValue(item) : item.value;
            const label = getItemLabel ? getItemLabel(item) : item.label;

            return (
              <RadioGroup.Item
                value={value}
                disabled={item.disabled}
                asChild
                onClick={() => onChange(value)}
                key={value}
              >
                {isByo ? (
                  <ByoOptionCard
                    option={item}
                    label={label}
                    choiceName={choice.name}
                    maxSelection={choice.maximumOptionLimit}
                  />
                ) : (
                  <OptionCard option={item} label={label} />
                )}
              </RadioGroup.Item>
            );
          })}
        </OptionGroup>
      </RadioGroup.Root>
    );
  }
);

RadioChoiceType.propTypes = {
  choice: ChoicePropType.isRequired,
  selectedOptions: SelectedOptionsPropType.isRequired,
  onChange: PropTypes.func.isRequired,
  index: PropTypes.number,
  options: PropTypes.arrayOf(OptionPropType),
  getItemValue: PropTypes.func,
  getItemLabel: PropTypes.func,
  isByo: PropTypes.bool,
};

RadioChoiceType.defaultProps = {
  index: undefined,
  options: undefined,
  getItemValue: undefined,
  getItemLabel: undefined,
  isByo: false,
};
